import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Button from '../Button';
import Container from '../Container';
import Message from '../Message';
import Segment from '../Segment';
import Form from '../Form';
import Icon from '../Icon';
import countryOptions from './country-options';
import messages from './messages';
import './styles.css';

const titleStatusOptions = new Map()
  .set('tasNo', 'ELIGIBLE_TAS_NO_PROGRAM')
  .set('tasTas', 'ELIGIBLE_TAS_PROGRAM')
  .set('swpTas', 'ELIGIBLE_SWP_TAS_PROGRAM')
  .set('swpNo', 'ELIGIBLE_SWP_NO_PROGRAM')
  .set('swpSwp', 'ELIGIBLE_SWP_SWP_PROGRAM')
  .set('noTasNoSwp', 'NOT_ELIGIBLE_SWP_TAS_PROGRAM')
  .set('unknown', 'UNKNOWN');

const titleEligibleOptions = new Map()
  .set('yes', 'YES')
  .set('no', 'NO')
  .set('unknown', 'UNKNOWN');

const charterOptions = new Map()
  .set('yes', 'YES')
  .set('no', 'NO')
  .set('unknown', 'UNKNOWN');

const coeducationalOptions = new Map()
  .set('coedOption', 'COED')
  .set('allMaleOption', 'ALL_MALE')
  .set('allFemaleOption', 'ALL_FEMALE')
  .set('unknown', 'UNKNOWN');

const gradeOptions = new Map()
  .set('preKindergartenStud', 'PRE_K')
  .set('kindergartenStud', 'K')
  .set(1, 'GRADE_1')
  .set(2, 'GRADE_2')
  .set(3, 'GRADE_3')
  .set(4, 'GRADE_4')
  .set(5, 'GRADE_5')
  .set(6, 'GRADE_6')
  .set(7, 'GRADE_7')
  .set(8, 'GRADE_8')
  .set(9, 'GRADE_9')
  .set(10, 'GRADE_10')
  .set(11, 'GRADE_11')
  .set(12, 'GRADE_12')
  .set('na', 'NA');

const schoolLevelOptions = new Map()
  .set('elementary', 'ELEMENTARY')
  .set('secondary', 'SECONDARY')
  .set('combined', 'COMBINED')
  .set('unknown', 'UNKNOWN');

const schoolPublicTypeOptions = new Map()
  .set('pubRegular', 'REGULAR_SCHOOL')
  .set('pubSpecial', 'SPECIAL_EDUCATION_SCHOOL')
  .set('pubVocational', 'VOCATIONAL_SCHOOL')
  .set('pubAlternative', 'OTHER_SCHOOL');

const schoolPrivateTypeOptions = new Map()
  .set('privRegular', 'REGULAR_ELEMENTARY_OR_SECONDARY')
  .set('privMontessori', 'MONTESSORI')
  .set('privSpecialProgram', 'SPECIAL_PROGRAM')
  .set('privSpecialEducation', 'SPECIAL_EDUCATION')
  .set('privCarTecVoc', 'CAREER_TECHNICAL_VOCATIONAL')
  .set('privEarly', 'EARLY_CHILDHOOD_OR_CARE_CENTER')
  .set('privAlternative', 'OTHER_SCHOOL');

class HSUpdateForm extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    requesting: PropTypes.bool.isRequired,
    updateError: PropTypes.bool.isRequired,
    highSchool: PropTypes.shape({
      name: PropTypes.string.isRequired,
      isPublic: PropTypes.bool.isRequired,
      titleStatus: PropTypes.oneOf([...titleStatusOptions.values()]),
      titleEligible: PropTypes.oneOf([...titleEligibleOptions.values()]),
      address: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
      county: PropTypes.string,
      country: PropTypes.oneOf([...countryOptions.values()]),
      phone: PropTypes.string,
      fax: PropTypes.string,
      website: PropTypes.string,
      charterSchool: PropTypes.oneOf([...charterOptions.values()]),
      coeducational: PropTypes.oneOf([...coeducationalOptions.values()]),
      collegeGoingRate: PropTypes.number,
      studentTeacherRatio: PropTypes.number,
      fullTimeTeachers: PropTypes.number,
      highestGrade: PropTypes.oneOf([...gradeOptions.values()]),
      lowestGrade: PropTypes.oneOf([...gradeOptions.values()]),
      schoolLevel: PropTypes.oneOf([...schoolLevelOptions.values()]),
      schoolPublicType: PropTypes.oneOf([...schoolPublicTypeOptions.values()]),
      schoolPrivateType: PropTypes.oneOf([...schoolPrivateTypeOptions.values()]),
      lastReportedYear: PropTypes.string,
      male: PropTypes.number,
      female: PropTypes.number,
      nonBinary: PropTypes.number,
      asianPacificIsl: PropTypes.number,
      amIndAlasNat: PropTypes.number,
      hawaiNatPcfIsl: PropTypes.number,
      twoOrMoreRaces: PropTypes.number,
      white: PropTypes.number,
      black: PropTypes.number,
      hispanic: PropTypes.number,
      preKindergarten: PropTypes.number,
      kindergarten: PropTypes.number,
      grade1: PropTypes.number,
      grade2: PropTypes.number,
      grade3: PropTypes.number,
      grade4: PropTypes.number,
      grade5: PropTypes.number,
      grade6: PropTypes.number,
      grade7: PropTypes.number,
      grade8: PropTypes.number,
      grade9: PropTypes.number,
      grade10: PropTypes.number,
      grade11: PropTypes.number,
      grade12: PropTypes.number,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = { ...props.highSchool };
    // make sure to turn all nulls into empty strings to make react happy
    Object.keys(this.state).forEach((key) => {
      if (this.state[key] === null) this.state[key] = '';
    });
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ highSchool, updateError, requesting }) {
    // only update highSchool if updateError and requesting did not change.
    // otherwise the current form could be overwriten with original values
    if (this.props.updateError === updateError && this.props.requesting === requesting) {
      const updatedForm = { ...highSchool };
      // make sure to turn all nulls into empty strings to make react happy
      Object.keys(updatedForm).forEach((key) => {
        if (updatedForm[key] === null) updatedForm[key] = '';
      });
      this.setState(updatedForm);
    }
  }

  /**
   * Set state with updated values.
   */
  onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onSubmit = (e) => {
    e.preventDefault();
    const updatedForm = { ...this.state };
    Object.keys(updatedForm).forEach((key) => {
      if (updatedForm[key] === '') updatedForm[key] = null;
    });
    this.props.onSave(updatedForm);
  };

  _onCancel = () => {
    this.props.onCancel();
  };

  _getOptions = (options) => (
    [...options.keys()].map((key) => (
      { key, text: this.props.intl.formatMessage(messages[key]), value: options.get(key) }
    ))
  );

  _getGradeOptions = () => (
    [...gradeOptions.keys()].map((key) => (
      {
        key,
        text: Number.isNaN(parseInt(key, 10)) ?
          this.props.intl.formatMessage(messages[key]) :
          this.props.intl.formatMessage(messages.gradeStudents, { grade: `${key}` }),
        value: gradeOptions.get(key),
      }
    ))
  );

  render() {
    const { highSchool, intl, updateError, requesting } = this.props;

    const errorDetails = updateError ? (
      <Message role="alert" negative>
        <Message.Header>
          <FormattedMessage {...messages.updateError} />
        </Message.Header>
      </Message>
    ) : '';

    return (
      <Container text className="HSUpdateFormPanel">
        <Form id="form-update-hs" onSubmit={this._onSubmit} size="large">
          <Segment>
            <h1 data-cy="hsupdate-institution-form-top-heading">{highSchool.name}</h1>
            <hr />
            <h2><FormattedMessage {...messages.contactInfo} /></h2>
            <Form.Field>
              <label htmlFor="address">
                <FormattedMessage {...messages.address} />
              </label>
              <input id="address" type="text" name="address" value={this.state.address} onChange={this.onChange} />
            </Form.Field>
            <Form.Field styleName="field-columns column-60">
              <label htmlFor="city">
                <FormattedMessage {...messages.city} />
              </label>
              <input id="city" type="text" name="city" value={this.state.city} onChange={this.onChange} />
            </Form.Field>
            <Form.Field styleName="field-columns column-15">
              <label htmlFor="state">
                <FormattedMessage {...messages.state} />
              </label>
              <input id="state" type="text" name="state" value={this.state.state} onChange={this.onChange} />
            </Form.Field>
            <Form.Field styleName="field-columns column-21">
              <label htmlFor="zip">
                <FormattedMessage {...messages.zip} />
              </label>
              <input id="zip" type="text" name="zip" value={this.state.zip} onChange={this.onChange} />
            </Form.Field>
            <Form.Field>
              <label htmlFor="county">
                <FormattedMessage {...messages.county} />
              </label>
              <input id="county" type="text" name="county" value={this.state.county} onChange={this.onChange} />
            </Form.Field>
            <Form.Select
              search
              id="country"
              name="country"
              styleName="field-columns column-50"
              label={intl.formatMessage(messages.country)}
              aria-label={intl.formatMessage(messages.country)}
              defaultValue={this.state.country}
              options={this._getOptions(countryOptions)}
              onChange={this.onChange}
            />
            <Form.Field>
              <label htmlFor="phone">
                <FormattedMessage {...messages.phone} />
              </label>
              <input
                id="phone"
                type="text"
                name="phone"
                value={this.state.phone}
                onChange={this.onChange}
                placeholder={intl.formatMessage(messages.phonePlaceholder)}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor="fax">
                <FormattedMessage {...messages.fax} />
              </label>
              <input
                id="fax"
                type="text"
                name="fax"
                value={this.state.fax}
                onChange={this.onChange}
                placeholder={intl.formatMessage(messages.faxPlaceholder)}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor="websiteUrl">
                <FormattedMessage {...messages.websiteUrl} />
              </label>
              <input
                id="websiteUrl"
                type="text"
                name="website"
                value={this.state.website}
                onChange={this.onChange}
                placeholder={intl.formatMessage(messages.websiteUrlPlaceholder)}
              />
            </Form.Field>
            <hr />
            <h2><FormattedMessage {...messages.enrollment} /></h2>
            <h3><FormattedMessage {...messages.enrollmentGender} /></h3>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="male">
                <FormattedMessage {...messages.male} />
              </label>
              <input id="male" type="number" min={0} name="male" value={this.state.male} onChange={this.onChange} />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="female">
                <FormattedMessage {...messages.female} />
              </label>
              <input id="female" type="number" min={0} name="female" value={this.state.female} onChange={this.onChange} />
            </Form.Field>
            <Form.Field styleName="column-50">
              <label htmlFor="nonBinary">
                <FormattedMessage {...messages.nonbinary} />
              </label>
              <input
                id="nonBinary"
                type="number"
                min={0}
                name="nonBinary"
                value={this.state.nonBinary}
                onChange={this.onChange}
              />
            </Form.Field>
            <h3><FormattedMessage {...messages.enrollmentRace} /></h3>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="asianpacificisl-stud">
                <FormattedMessage {...messages.asianpacificisl} />
              </label>
              <input
                id="asianpacificisl-stud"
                type="number"
                min={0}
                name="asianPacificIsl"
                value={this.state.asianPacificIsl}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="am-ind-alas-nat-stud">
                <FormattedMessage {...messages.amIndAlasNat} />
              </label>
              <input
                id="am-ind-alas-nat-stud"
                type="number"
                min={0}
                name="amIndAlasNat"
                value={this.state.amIndAlasNat}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="hawai-natpcfisl-stud">
                <FormattedMessage {...messages.hawaiNatpcfIsl} />
              </label>
              <input
                id="hawai-natpcfisl-stud"
                type="number"
                min={0}
                name="hawaiNatPcfIsl"
                value={this.state.hawaiNatPcfIsl}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="hispanic-stud">
                <FormattedMessage {...messages.hispanic} />
              </label>
              <input
                id="hispanic-stud"
                type="number"
                min={0}
                name="hispanic"
                value={this.state.hispanic}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="black-stud">
                <FormattedMessage {...messages.blackAfrican} />
              </label>
              <input
                id="black-stud"
                type="number"
                min={0}
                name="black"
                value={this.state.black}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="white-stud">
                <FormattedMessage {...messages.white} />
              </label>
              <input
                id="white-stud"
                type="number"
                min={0}
                name="white"
                value={this.state.white}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="column-50">
              <label htmlFor="twoormore-races-stud">
                <FormattedMessage {...messages.twoormoreRaces} />
              </label>
              <input
                id="twoormore-races-stud"
                type="number"
                min={0}
                name="twoOrMoreRaces"
                value={this.state.twoOrMoreRaces}
                onChange={this.onChange}
              />
            </Form.Field>
            <h3><FormattedMessage {...messages.enrollmentGrade} /></h3>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="preKindergarten">
                <FormattedMessage {...messages.preKindergartenStud} />
              </label>
              <input
                id="preKindergarten"
                type="number"
                min={0}
                name="preKindergarten"
                value={this.state.preKindergarten}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="kindergarten">
                <FormattedMessage {...messages.kindergartenStud} />
              </label>
              <input
                id="kindergarten"
                type="number"
                min={0}
                name="kindergarten"
                value={this.state.kindergarten}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="grade-1-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '1' }} />
              </label>
              <input
                id="grade-1-stud"
                type="number"
                min={0}
                name="grade1"
                value={this.state.grade1}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="grade-2-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '2' }} />
              </label>
              <input
                id="grade-2-stud"
                type="number"
                min={0}
                name="grade2"
                value={this.state.grade2}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="grade-3-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '3' }} />
              </label>
              <input
                id="grade-3-stud"
                type="number"
                min={0}
                name="grade3"
                value={this.state.grade3}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="grade-4-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '4' }} />
              </label>
              <input
                id="grade-4-stud"
                type="number"
                min={0}
                name="grade4"
                value={this.state.grade4}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="grade-5-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '5' }} />
              </label>
              <input
                id="grade-5-stud"
                type="number"
                min={0}
                name="grade5"
                value={this.state.grade5}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="grade-6-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '6' }} />
              </label>
              <input
                id="grade-6-stud"
                type="number"
                min={0}
                name="grade6"
                value={this.state.grade6}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="grade-7-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '7' }} />
              </label>
              <input
                id="grade-7-stud"
                type="number"
                min={0}
                name="grade7"
                value={this.state.grade7}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="grade-8-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '8' }} />
              </label>
              <input
                id="grade-8-stud"
                type="number"
                min={0}
                name="grade8"
                value={this.state.grade8}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="grade-9-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '9' }} />
              </label>
              <input
                id="grade-9-stud"
                type="number"
                min={0}
                name="grade9"
                value={this.state.grade9}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="grade-10-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '10' }} />
              </label>
              <input
                id="grade-10-stud"
                type="number"
                min={0}
                name="grade10"
                value={this.state.grade10}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50">
              <label htmlFor="grade-11-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '11' }} />
              </label>
              <input
                id="grade-11-stud"
                type="number"
                min={0}
                name="grade11"
                value={this.state.grade11}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Field styleName="field-columns column-50 margin-left-2">
              <label htmlFor="grade-12-stud">
                <FormattedMessage {...messages.gradeStudents} values={{ grade: '12' }} />
              </label>
              <input
                id="grade-12-stud"
                type="number"
                min={0}
                name="grade12"
                value={this.state.grade12}
                onChange={this.onChange}
              />
            </Form.Field>
            <h3><FormattedMessage {...messages.titleInformation} /></h3>
            <Form.Select
              id="titleEligible"
              name="titleEligible"
              styleName="field-columns column-50"
              label={intl.formatMessage(messages.titleEligible)}
              defaultValue={this.state.titleEligible}
              options={this._getOptions(titleEligibleOptions)}
              onChange={this.onChange}
            />
            <Form.Select
              id="titleStatus"
              name="titleStatus"
              styleName="field-columns column-50 margin-left-2"
              label={intl.formatMessage(messages.titleStatus)}
              defaultValue={this.state.titleStatus}
              options={this._getOptions(titleStatusOptions)}
              onChange={this.onChange}
            />
            <hr />
            <h2><FormattedMessage {...messages.academicData} /></h2>
            <Form.Select
              id="charterSchool"
              name="charterSchool"
              styleName="field-columns column-50"
              label={intl.formatMessage(messages.charterSchool)}
              defaultValue={this.state.charterSchool}
              options={this._getOptions(charterOptions)}
              onChange={this.onChange}
            />
            <Form.Select
              id="coeducational"
              name="coeducational"
              styleName="field-columns column-50 margin-left-2"
              label={intl.formatMessage(messages.coeducational)}
              defaultValue={this.state.coeducational}
              options={this._getOptions(coeducationalOptions)}
              onChange={this.onChange}
            />
            <Form.Field>
              <label htmlFor="collegeRate">
                <FormattedMessage {...messages.collegeRate} />
              </label>
              <input
                id="collegeRate"
                type="number"
                min={0}
                name="collegeGoingRate"
                value={this.state.collegeGoingRate}
                onChange={this.onChange}
                placeholder={intl.formatMessage(messages.collegeRatePlaceholder)}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor="ratio">
                <FormattedMessage {...messages.ratio} />
              </label>
              <input
                id="ratio"
                type="number"
                min={0}
                step="0.01"
                name="studentTeacherRatio"
                value={this.state.studentTeacherRatio}
                onChange={this.onChange}
                placeholder={intl.formatMessage(messages.ratioPlaceholder)}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor="fullTime">
                <FormattedMessage {...messages.fullTime} />
              </label>
              <input
                id="fullTime"
                type="number"
                min={0}
                step="0.01"
                name="fullTimeTeachers"
                value={this.state.fullTimeTeachers}
                onChange={this.onChange}
              />
            </Form.Field>
            <Form.Select
              id="highestGrade"
              name="highestGrade"
              styleName="field-columns column-50"
              label={intl.formatMessage(messages.highestGrade)}
              defaultValue={this.state.highestGrade}
              options={this._getGradeOptions()}
              onChange={this.onChange}
            />
            <Form.Select
              id="lowestGrade"
              name="lowestGrade"
              styleName="field-columns column-50 margin-left-2"
              label={intl.formatMessage(messages.lowestGrade)}
              defaultValue={this.state.lowestGrade}
              options={this._getGradeOptions()}
              onChange={this.onChange}
            />
            <Form.Select
              id="schoolLevel"
              name="schoolLevel"
              label={intl.formatMessage(messages.schoolLevel)}
              defaultValue={this.state.schoolLevel}
              options={this._getOptions(schoolLevelOptions)}
              onChange={this.onChange}
            />
            <Form.Select
              id={highSchool.isPublic ? 'schoolPublicType' : 'schoolPrivateType'}
              name={highSchool.isPublic ? 'schoolPublicType' : 'schoolPrivateType'}
              label={intl.formatMessage(messages.schoolTypeOptions)}
              defaultValue={highSchool.isPublic ? this.state.schoolPublicType : this.state.schoolPrivateType}
              options={this._getOptions(highSchool.isPublic ? schoolPublicTypeOptions : schoolPrivateTypeOptions)}
              onChange={this.onChange}
            />
            <Form.Field>
              <label htmlFor="schoolLastData">
                <FormattedMessage {...messages.schoolLastData} />
              </label>
              <input
                id="schoolLastData"
                type="text"
                name="lastReportedYear"
                value={this.state.lastReportedYear}
                onChange={this.onChange}
              />
            </Form.Field>
            <Button type="button" onClick={this._onCancel}>
              <Icon name="remove" />
              <FormattedMessage id="common.button.cancel" defaultMessage="Cancel" />
            </Button>
            <Button primary type="submit" data-cy="hsupdate-institution-form-save-btn" loading={requesting} aria-busy={requesting}>
              <Icon name="checkmark" />
              <FormattedMessage id="common.button.requestUser" defaultMessage="Save" />
            </Button>
          </Segment>
        </Form>
        {errorDetails}
      </Container>
    );
  }
}

export default injectIntl(HSUpdateForm);
